export const endpointPath = (nodeId, endpointId) => {
  return `${nodeId}.${endpointId}`
}

export const fromEndpointPath = (path) => {
  const bits = path.split('.');
  return {
    nodeId: bits[0],
    endpointId: bits[1]
  }
}

/**
 * Check if a value is a plain object (not null, not an array).
 * Used to distinguish between compact config (direct value) and full config ({ value, ... }).
 */
export const isObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Check if a config entry is in full format (wrapped with metadata).
 */
export const isWrappedConfig = (value: unknown): value is { value: unknown } => {
  return isObject(value) && 'value' in value;
};

/**
 * Get config as object for accessing metadata (min, max, etc.)
 * Returns empty object for raw values.
 */
export const toConfigObject = (value: unknown): Record<string, unknown> => {
  return isObject(value) ? value : {};
};

/**
 * Extract the actual value from a config entry.
 * Config can be stored in two formats:
 * - Compact: just the value (e.g., 24, [1,2,3])
 * - Full: object with value and metadata (e.g., { value: 24, isInput: true })
 */
export const resolveConfigValue = (config: unknown): unknown => {
  if (isWrappedConfig(config)) {
    return config.value;
  }
  return config;
}

// ─────────────────────────────────────────────────────────────
// Value Scaling & Clamping
// ─────────────────────────────────────────────────────────────

/**
 * Scale a value from source range to target range.
 */
export function scaleValue(
  value: number,
  source: { min: number; max: number },
  target: { min: number; max: number }
): number {
  const sourceRange = source.max - source.min;
  const targetRange = target.max - target.min;
  if (sourceRange === 0) return target.min;
  const normalized = (value - source.min) / sourceRange;
  return target.min + normalized * targetRange;
}

/**
 * Clamp a value to a range.
 */
export function clampToRange(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// ─────────────────────────────────────────────────────────────
// Endpoint Resolution
// ─────────────────────────────────────────────────────────────

/**
 * Resolve an endpoint name through an API (exposed endpoints).
 * Returns the internal path if exposed, otherwise the original endpoint.
 */
export function resolveExposedPath(
  api: Record<string, { path: string }>,
  endpoint: string
): string {
  return api[endpoint]?.path ?? endpoint;
}

/**
 * Determine default isInput/isOutput for an exposed endpoint based on internal endpoint.
 * Connection types (audio, video, event, trigger) preserve direction.
 * Data types only expose as output by default.
 */
export function getExposedEndpointDefaults(
  internalEndpoint: { dataType?: string; dataFlow?: string; isInput?: boolean; isOutput?: boolean }
): { isInput: boolean; isOutput: boolean } {
  const dataType = internalEndpoint.dataType;
  const dataFlow = internalEndpoint.dataFlow;
  const isInput = internalEndpoint.isInput ?? false;
  const isOutput = internalEndpoint.isOutput ?? false;

  // Connection types: audio, video, OR event/trigger flow
  const isConnectionType = dataType === 'audio' || dataType === 'video' || dataFlow === 'event' || dataFlow === 'trigger';

  if (isConnectionType) {
    // For connection types, expose in the same direction as internal
    // But if both, prefer output only
    if (isInput && !isOutput) {
      return { isInput: true, isOutput: false };
    } else if (isOutput) {
      return { isInput: false, isOutput: true };
    } else {
      return { isInput: false, isOutput: false };
    }
  } else {
    // For data types, only expose as output if internal is output
    // Never expose as input by default (user can override)
    if (isOutput) {
      return { isInput: false, isOutput: true };
    } else {
      return { isInput: false, isOutput: false };
    }
  }
}